/* eslint-disable react-hooks/exhaustive-deps */
import { withRouter } from "../_shared/withRouter";
import React, { useEffect, useState, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Header from "../template/_common/header";
import SidebarComponent from "../template/admin/sidebarComponent";
import SidebarArrow from "../template/_common/sidebarArrow";
import LmsLearningContentStep from "../template/admin/lmsLearningContentStep";
import LmsAudienceAssets from "../template/admin/lmsAudienceAssets";
import LmsReadinessPanel from "../template/admin/lmsReadinessPanel";
import { globalAlert, globalLoader } from "../actions/commonActions";
import { logOut } from "../_shared/commonFunction";
import {
  SERVICE_AUDIENCES,
  lmsGetCourseApi,
  lmsSaveCourseApi,
  lmsUpdateCourseStatusApi,
  lmsAddCourseIntroAssetApi,
  lmsDeleteCourseIntroAssetApi,
  lmsCourseReadinessApi,
} from "./_lmsActions";
import {
  parseCourseDetail,
  computeReadiness,
  assetPayload,
  mergeWrappedAssetsByKey,
} from "./_lmsHelpers";

const STEPS = [
  { n: 1, key: "info", icon: "fas fa-info-circle" },
  {
    n: 2,
    key: "courseIntro",
    icon: "fas fa-video",
    labelKey: "admin.lms.course.introVideos",
  },
  { n: 3, key: "learningContent", icon: "fas fa-layer-group" },
  { n: 4, key: "reviewPublish", icon: "fas fa-rocket" },
];

const LAST_STEP = 4;
const DESCRIPTION_MAX_LENGTH = 5000;
const OPTIONAL_REVIEW_ITEM_PATTERN = /key tips|local support|support service/i;

const requiredServerModuleItems = (moduleItem = {}, index = 0) => {
  const items =
    moduleItem.remainingItems ||
    (moduleItem.issues || []).map((label, issueIndex) => ({
      code: `${moduleItem.moduleId || index}-${issueIndex}`,
      label,
    }));
  return (items || []).filter(
    (item) => !OPTIONAL_REVIEW_ITEM_PATTERN.test(item?.label || item || "")
  );
};

const serverReadinessWithoutOptionalAssets = (readiness = null) => {
  if (!readiness) return null;
  const c = readiness.checks || {};
  const courseItems = readiness.courseItems || [];
  const sourceModules = readiness.modules || c.modules || [];
  const courseReady = courseItems.length
    ? courseItems.every((item) => item.complete)
    : !!c.courseInfo && !!c.introVideoESHS && !!c.introVideoSSHS && !!c.hasModules;
  const modules = sourceModules.map((moduleItem, index) => {
    const requiredItems = requiredServerModuleItems(moduleItem, index);
    return {
      ...moduleItem,
      remainingItems: requiredItems,
      ready: requiredItems.length === 0,
    };
  });
  const ready = courseReady && modules.every((moduleItem) => moduleItem.ready);
  return {
    ...readiness,
    modules,
    canPublish: ready,
    ready,
  };
};

const stepFromSearch = (search = "") => {
  const requested = Number(new URLSearchParams(search).get("step"));
  return Number.isInteger(requested) && requested >= 1 && requested <= LAST_STEP
    ? requested
    : null;
};

const defaultCourseForm = {
  title: "",
  description: "",
  hasQuizPerSection: true,
  sequentialModules: false,
  requireIntroVideo: true,
  requireAllModules: true,
  requireSectionQuizPass: true,
};

const courseFormFromCourse = (course = {}) => ({
  title: course?.title || "",
  description: course?.description || "",
  hasQuizPerSection: course?.hasQuizPerSection ?? true,
  sequentialModules: !!course?.sequentialModules,
  requireIntroVideo: course?.completionCriteria?.requireIntroVideo ?? true,
  requireAllModules: course?.completionCriteria?.requireAllModules ?? true,
  requireSectionQuizPass:
    course?.completionCriteria?.requireSectionQuizPass ?? true,
});

const courseFormChanged = (current, saved) =>
  JSON.stringify(current || defaultCourseForm) !==
  JSON.stringify(saved || defaultCourseForm);

const sectionsOf = (module) => module?.sections || [];
const assetIsFilled = (asset) =>
  !!(asset?.url || asset?.content || asset?.signedUrl || asset?.fileUrl);
const sectionHasAssetType = (section, type) =>
  (section?.assets || []).some((asset) => asset?.type === type && assetIsFilled(asset));
const sectionHasQuiz = (section) => !!(section?.quizDoc || section?.quiz);
const moduleHasIntroVideo = (module) =>
  (module?.introductionAssets || []).some((asset) => asset?.type === "video" && assetIsFilled(asset));

const learningContentNavigationIssues = (modules = []) => {
  if (!modules.length) {
    return ["Please add at least one module."];
  }

  return [];
};

const learningContentIssues = (modules = [], requireSectionQuiz = true) => {
  const issues = [];

  if (!modules.length) {
    return ["Please add at least one module."];
  }

  modules.forEach((module, moduleIndex) => {
    const moduleTitle = module?.title || `Module ${moduleIndex + 1}`;
    const sections = sectionsOf(module);

    if (!sections.length) {
      issues.push(`${moduleTitle}: add at least one part.`);
      return;
    }

    sections.forEach((section, sectionIndex) => {
      const partTitle = section?.title || `Part ${sectionIndex + 1}`;
      if (!sectionHasAssetType(section, "video")) {
        issues.push(`${moduleTitle} - ${partTitle}: upload a video.`);
      }
      if (requireSectionQuiz && !sectionHasQuiz(section)) {
        issues.push(`${moduleTitle} - ${partTitle}: add a quiz.`);
      }
    });

  });

  return issues;
};

const moduleContentStatus = (modules = [], requireSectionQuiz = true) => {
  let completedItems = 0;
  let totalItems = 0;
  let completedModules = 0;
  const remaining = [];

  modules.forEach((module, moduleIndex) => {
    const moduleTitle = module?.title || `Module ${moduleIndex + 1}`;
    const missing = [];
    const sections = sectionsOf(module);

    const track = (ready, label) => {
      totalItems += 1;
      if (ready) completedItems += 1;
      else missing.push(label);
    };

    track(moduleHasIntroVideo(module), "module introduction video");

    if (!sections.length) {
      track(false, "at least one part");
    } else {
      sections.forEach((section, sectionIndex) => {
        const partTitle = section?.title || `Part ${sectionIndex + 1}`;
        track(sectionHasAssetType(section, "video"), `${partTitle} video`);
        if (requireSectionQuiz) track(sectionHasQuiz(section), `${partTitle} quiz`);
      });
    }

    if (missing.length) {
      remaining.push({ moduleTitle, missing });
    } else {
      completedModules += 1;
    }
  });

  return {
    completedItems,
    totalItems,
    completedModules,
    remainingModules: remaining.length,
    remaining,
  };
};

const LmsCourseWizard = (props) => {
  const { t } = useTranslation();
  const routeCourseId = props?.match?.params?.id || null;
  const isEditMode = !!routeCourseId;
  const requestedStep = stepFromSearch(props?.location?.search);
  const [navbarToggle, setNavbarToggle] = useState(false);
  const [step, setStep] = useState(isEditMode ? requestedStep || 1 : 1);
  const [reached, setReached] = useState(isEditMode ? LAST_STEP : 1);
  const [courseId, setCourseId] = useState(routeCourseId);

  const [course, setCourse] = useState(null);
  const [modules, setModules] = useState([]);
  const [published, setPublished] = useState(false);


  const [form, setForm] = useState(defaultCourseForm);
  const [savedForm, setSavedForm] = useState(defaultCourseForm);
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [learningStatusModal, setLearningStatusModal] = useState(null);
  const learningContentGuardRef = useRef(null);
  const [wizardDirty, setWizardDirty] = useState(false);
  // review & publish step
  const [serverReadiness, setServerReadiness] = useState(null);
  const courseRecordId = course?._id || course?.courseId || "";
  const apiCourseId = courseRecordId || courseId || "";
  const courseRouteIdentifier = course?.slug || courseRecordId || courseId || "";
  const courseInfoDirty = !!thumbnailFile || courseFormChanged(form, savedForm);
  const runWithLearningContentGuard = useCallback((action) => {
    const guard = learningContentGuardRef.current;
    if (typeof guard === "function") return guard(action);
    action?.();
    return true;
  }, []);

  const loadReadiness = async (id = apiCourseId) => {
    if (!id) return;
    try {
      const res = await lmsCourseReadinessApi(id);
      if (res?.data?.status === 200) {
        console.log("server readiness", res);
        setServerReadiness(res?.data?.data?.readiness || res?.data?.data || null);
      }
    } catch (e) {
      /* non-fatal — the client readiness ring still shows */
    }
  };

  const setField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));

    setWizardDirty(true);
  };

  const handleThumbnailFile = (file) => {
    if (!file) {
      setThumbnailFile(null);
      setPreviewUrl(null);
      return;
    }
    if (!file.type?.startsWith("image/")) {
      globalAlert(
        "error",
        t("admin.lms.course.thumbnailImageOnly", "Please upload an image file.")
      );
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      globalAlert(
        "error",
        t(
          "admin.lms.course.thumbnailTooLarge",
          "Thumbnail image must be 2MB or smaller."
        )
      );
      return;
    }
    setThumbnailFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    setWizardDirty(true);
  };

  const loadCourse = async (id = courseId, options = {}) => {
    if (!id) return;
    const preserveIntroAssets = !!options?.preserveIntroAssets;
    try {
      globalLoader(true);
      const res = await lmsGetCourseApi(id);
      if (res?.data?.status === 200) {
        const { course: c, modules: m } = parseCourseDetail(res?.data?.data);
        const isReadOnlyArchivedCourse =
          c?.status === "archived" && Number(c?.publishedVersion || 0) > 0;
        if (isReadOnlyArchivedCourse) {
          const publishedIdentifier = c?.slug || c?._id || id;
          globalLoader(false);
          globalAlert(
            "warning",
            "This archived course is read-only. Copy it to create an editable draft."
          );
          props?.history?.replace(
            `/admin/lms/course/detail/${publishedIdentifier}`
          );
          return;
        }
        setPublished(res?.data?.data?.course?.status === "published");
        setCourse((prev) =>
          preserveIntroAssets
            ? {
              ...c,
              introAssets: mergeWrappedAssetsByKey(
                prev?.introAssets,
                c?.introAssets,
                "serviceAudience"
              ),
            }
            : c
        );
        setModules(m);
        const loadedForm = courseFormFromCourse(c);
        setForm(loadedForm);
        setSavedForm(loadedForm);
        setThumbnailFile(null);
        setPreviewUrl(c?.thumbnailUrl || null);
        if (c?.slug && id === routeCourseId && c.slug !== routeCourseId) {
          props?.history?.replace(`/admin/lms/course/${c.slug}${props?.location?.search || ""}`);
        }
      }
      globalLoader(false);
    } catch (error) {
      globalLoader(false);
      if (error?.response?.data?.status === 401)
        logOut(props?.history, "/admin/login");
    }
  };

  useEffect(() => {
    if (!routeCourseId) return;
    setCourseId(routeCourseId);
    setReached(LAST_STEP);
    if (requestedStep) setStep(requestedStep);
    loadCourse(routeCourseId);
  }, [routeCourseId, props?.location?.search]);

  useEffect(() => {
    if (step === LAST_STEP && apiCourseId) loadReadiness(apiCourseId);
  }, [step, apiCourseId]);

  // Persist Step 1. Returns the course id on success.
  const saveCourseInfo = async () => {
    const existingId = courseRecordId || courseId || "";
    if (!courseInfoDirty && existingId) return existingId;

    if (!form.title?.trim()) {
      globalAlert("error", t("admin.lms.course.titleRequired"));
      return null;
    }
    try {
      globalLoader(true);
      const payload = {
        courseId: courseRecordId || courseId || "",
        title: form.title,
        description: form.description,
        hasQuizPerSection: form.hasQuizPerSection,
        sequentialModules: form.sequentialModules,
        completionCriteria: {
          requireIntroVideo: form.requireIntroVideo,
          requireAllModules: form.requireAllModules,
          requireSectionQuizPass: form.requireSectionQuizPass,
        },
        ...(thumbnailFile ? { thumbnailFile } : {}),
      };
      const res = await lmsSaveCourseApi(payload);
      if (res?.data?.status === 200) {
        const c = res?.data?.data?.course || res?.data?.data || {};
        const id = c?._id || c?.courseId || courseId;
        const savedSnapshot = courseFormFromCourse({ ...c, ...payload });
        setCourseId(id);
        setCourse(c);
        setSavedForm(savedSnapshot);
        setThumbnailFile(null);
        globalLoader(false);
        globalAlert("success", res?.data?.message);
        return id;
      }
      globalAlert("error", res?.data?.message);
      globalLoader(false);
      return null;
    } catch (error) {
      globalLoader(false);
      globalAlert("error", error?.response?.data?.message);
      return null;
    }
  };

  const goToStep = (n) => {
    if (n > reached) return;
    setStep(n);
    if (n === LAST_STEP) loadReadiness();
  };

  const validateCurrentStep = () => {
    if (step === 3) {
      const issues = learningContentNavigationIssues(modules);

      if (issues.length) {
        globalAlert(
          "error",
          t(
            "admin.lms.wizard.completeLearningContent",
            "Please complete all required learning content before continuing."
          ) + ` ${issues[0]}`
        );
        return false;
      }
    }

    return true;
  };

  const goNextStep = () => {
    const n = Math.min(step + 1, LAST_STEP);
    setStep(n);
    setReached((r) => Math.max(r, n));
    if (n === LAST_STEP) loadReadiness();
  };

  const next = async () => {
    if (step === 1 || courseInfoDirty) {
      const shouldReload = courseInfoDirty;
      const id = await saveCourseInfo();
      if (!id) return;
      if (shouldReload) await loadCourse(id);
    }

    if (step !== 1 && !validateCurrentStep()) {
      return;
    }

    if (step === 3) {
      const requireSectionQuiz =
        form.hasQuizPerSection || form.requireSectionQuizPass;
      const status = moduleContentStatus(
        modules,
        requireSectionQuiz
      );
      if (!status.remainingModules && !status.remaining.length) {
        goNextStep();
        return;
      }
      setLearningStatusModal({
        status,
      });
      return;
    }

    goNextStep();
  };

  const confirmLearningStatusAndNext = () => {
    setLearningStatusModal(null);
    goNextStep();
  };

  const back = () => setStep((s) => Math.max(1, s - 1));

  // Save course info but stay on the current step; intro assets attach through
  // course-scoped routes, so a course id must exist before uploads are enabled.
  const saveStay = async () => {
    if (!courseInfoDirty && apiCourseId) return;
    const id = await saveCourseInfo();
    if (id) await loadCourse(id);
  };

  const addCourseIntro = async (audience, asset) => {
    try {
      globalLoader(true);
      const res = await lmsAddCourseIntroAssetApi(apiCourseId, {
        serviceAudience: audience,
        asset: assetPayload(asset),
      });
      globalLoader(false);
      if (res?.data?.status === 200) {
        setWizardDirty(true);
        await loadCourse(apiCourseId, { preserveIntroAssets: true });
        return true;
      }
      globalAlert("error", res?.data?.message);
      return false;
    } catch (e) {
      globalLoader(false);
      globalAlert("error", e?.response?.data?.message);
      return false;
    }
  };
  const removeCourseIntro = async (wrapperId) => {
    try {
      globalLoader(true);
      const res = await lmsDeleteCourseIntroAssetApi(apiCourseId, wrapperId);
      globalLoader(false);
      if (res?.data?.status === 200) {
        setWizardDirty(true);
        loadCourse();
      }
      else globalAlert("error", res?.data?.message);
    } catch (e) {
      globalLoader(false);
      globalAlert("error", e?.response?.data?.message);
    }
  };

  const saveDraftAndExit = async () => {
    if (courseInfoDirty || !apiCourseId) {
      const id = await saveCourseInfo();
      if (!id) return;
    }
    if (published) {
      await publishCourse();
      return;
    }
    setWizardDirty(false);
    props?.history?.push("/admin/lms/courses");
  };

  /* ── publish ── */
  const readiness = course
    ? computeReadiness(course, modules, null, t)
    : { checks: [], score: 0 };

  const publishCourse = async () => {
    const wasPublished = published;
    try {
      let publishCourseId = apiCourseId;
      if (courseInfoDirty || !publishCourseId) {
        publishCourseId = await saveCourseInfo();
        if (!publishCourseId) return;
      }
      globalLoader(true);
      const res = await lmsUpdateCourseStatusApi(publishCourseId, "published");
      if (res?.data?.status === 200) {
        globalAlert(
          "success",
          wasPublished ? "Course changes saved." : res?.data?.message
        );
        globalLoader(false);
        setPublished(true);
        setWizardDirty(false);
        props?.history?.push(
          `/admin/lms/course/detail/${publishCourseId}`
        );
      } else {
        globalAlert("error", res?.data?.message);
        globalLoader(false);
      }
    } catch (error) {
      globalAlert("error", error?.response?.data?.message);
      globalLoader(false);
    }
  };

  const nextStepLabel = "Next";
  const step3NavigationIssues = learningContentNavigationIssues(modules);
  const step3Issues = learningContentIssues(
    modules,
    form.hasQuizPerSection || form.requireSectionQuizPass
  );
  const courseIntroVideosReady = SERVICE_AUDIENCES.every((audience) =>
    (course?.introAssets || []).some((item) => item?.serviceAudience === audience && item?.asset)
  );
  const nextBlocked = 
    (step === 1 && !form.title?.trim()) ||
    (step === 3 && step3NavigationIssues.length > 0);
  const clientPublishReady =
    !!course?.title &&
    courseIntroVideosReady &&
    modules.length > 0 &&
    step3Issues.length === 0;
  const reviewReadiness = serverReadinessWithoutOptionalAssets(serverReadiness);
  const publishReady = serverReadiness
    ? !!(reviewReadiness?.canPublish ?? reviewReadiness?.ready)
    : clientPublishReady;
  const pageTitle = isEditMode
    ? t("admin.lms.wizard.editTitle", "Edit course")
    : t("admin.lms.wizard.title");
  const openLearnerPreview = () => {
    if (!courseRouteIdentifier) return;
    const returnTo = `/admin/lms/course/${courseRouteIdentifier}?step=${LAST_STEP}`;
    props?.history?.push({
      pathname: `/admin/lms/course/${courseRouteIdentifier}/preview`,
      search: `?returnTo=${encodeURIComponent(returnTo)}`,
      state: { returnTo },
    });
  };
  return (
    <>
      <Helmet>
        <title>{pageTitle} - eC-Card</title>
        <meta name="viewport" content="width=1200, initial-scale=1" />
      </Helmet>
      <div className="admin-portal">
        <Header roleType="admin" />
        <div className={`${navbarToggle ? "active" : ""} wrapper`}>
          <SidebarComponent
            navbarToggle={navbarToggle}
            roleType="admin"
            menu="lms"
            subMenu="lms-courses"
          />
          <div className="main">
            <div className="main-wrapper">
              <div>
                <p className="admin-banner">{pageTitle}</p>
              </div>
              <SidebarArrow handleSidebarToggle={setNavbarToggle} />

              <div className="container-fluid pt-3 lms-course-wizard-page">
                <div className="lms-breadcrumb mb-3">
                  <Link to="/admin/lms/courses">
                    {t("admin.lms.courses.title")}
                  </Link>{" "}
                  <i className="fas fa-chevron-right mx-1"></i>{" "}
                  {pageTitle}
                </div>

                {/* Stepper */}
                <div className="lms-wizard-steps mb-4">
                  {STEPS.map((s) => (
                    <button
                      key={s.n}
                      className={`lms-wizard-step ${step === s.n ? "active" : ""
                        } ${reached > s.n ? "done" : ""} ${s.n > reached ? "locked" : ""
                        }`}
                      onClick={() =>
                        runWithLearningContentGuard(() => goToStep(s.n))
                      }
                      disabled={s.n > reached}
                    >
                      <span className="lms-wizard-step-icon">
                        {reached > s.n ? (
                          <i className="fas fa-check"></i>
                        ) : (
                          <i className={s.icon}></i>
                        )}
                      </span>
                      <span className="lms-wizard-step-label">
                        <span className="lms-wizard-step-n">
                          {t("admin.lms.wizard.stepN", { n: s.n })}
                        </span>
                        <span className="lms-wizard-step-name">
                          {t(s.labelKey || `admin.lms.wizard.step_${s.key}`)}
                          {s.optional && (
                            <span className="lms-optional-chip">
                              {t("common.optional", "Optional")}
                            </span>
                          )}
                        </span>
                      </span>
                    </button>
                  ))}
                </div>

                {/* ── Step 1: Course Information ── */}
                {step === 1 && (
                  <div className="card lms-form-card lms-course-info-card mb-3">
                    <div className="card-body">
                      <h3 className="lms-section-title">
                        {t("admin.lms.wizard.step_info")}
                      </h3>
                      <div className="lms-course-info-layout">
                        <div className="lms-course-info-main">
                          <div className="lms-form-field">
                            <div className="lms-field-label-row">
                              <label className="form-own-label">
                                {t("admin.lms.fields.courseTitle")} *
                              </label>
                              <span className="lms-char-count">
                                {form.title.length} / 150
                              </span>
                            </div>
                            <input
                              className="form-control lms-course-title-input"
                              value={form.title}
                              maxLength={150}
                              placeholder={t(
                                "admin.lms.course.titlePlaceholder",
                                "Enter course title"
                              )}
                              onChange={(e) => setField("title", e.target.value)}
                            />
                          </div>

                          <div className="lms-form-field">
                            <label className="form-own-label">
                              {t("common.description")}
                            </label>
                            <div className="lms-rich-text-shell">
                              <div className="lms-description-wrap">
                                <textarea
                                  className="form-control lms-description-input"
                                  rows={10}
                                  value={form.description}
                                  maxLength={DESCRIPTION_MAX_LENGTH}
                                  placeholder={t(
                                    "admin.lms.course.descriptionPlaceholder",
                                    "Enter course description..."
                                  )}
                                  onChange={(e) => {
                                    setField("description", e.target.value);
                                  }}
                                />
                              </div>
                            </div>
                            <span className="lms-description-count">
                              {form.description.length} /{" "}
                              {DESCRIPTION_MAX_LENGTH}
                            </span>
                          </div>

                        </div>

                        <div className="lms-thumbnail-card">
                          <h4>
                            {t("admin.lms.course.thumbnailTitle", "Thumbnail image")}
                          </h4>
                          <p>
                            {t(
                              "admin.lms.course.thumbnailHelp",
                              "This image will represent your course in the course list."
                            )}
                          </p>
                          <input
                            id="course-thumbnail-upload"
                            type="file"
                            className="lms-file-input"
                            accept="image/*"
                            onChange={(e) =>
                              handleThumbnailFile(e.target.files?.[0] || null)
                            }
                          />
                          <label
                            className={`lms-thumbnail-dropzone ${previewUrl ? "has-preview" : ""
                              }`}
                            htmlFor="course-thumbnail-upload"
                            onDragOver={(e) => e.preventDefault()}
                            onDrop={(e) => {
                              e.preventDefault();
                              handleThumbnailFile(e.dataTransfer.files?.[0] || null);
                            }}
                          >
                            {previewUrl ? (
                              <img src={previewUrl} alt="Course thumbnail" />
                            ) : (
                              <span className="lms-thumbnail-empty">
                                <span className="lms-thumbnail-icon-wrap">
                                  <i className="fas fa-image"></i>
                                  <span>
                                    <i className="fas fa-upload"></i>
                                  </span>
                                </span>
                                <strong>
                                  {t(
                                    "admin.lms.course.thumbnailUploadTitle",
                                    "Upload Course Thumbnail"
                                  )}
                                </strong>
                                <em>
                                  {t(
                                    "admin.lms.course.thumbnailUploadHint",
                                    "Drag and drop an image here, or click to browse"
                                  )}
                                </em>
                                {/* <small>
                                  {t(
                                    "admin.lms.course.thumbnailRecommended",
                                    "Recommended size: 1280 x 720 px (16:9)"
                                  )}
                                </small> */}
                                {/* <small>
                                  {t(
                                    "admin.lms.course.thumbnailLimit",
                                    "Max file size: 2MB - JPG, PNG"
                                  )}
                                </small> */}
                                <span className="btn secondary-btn btn-with-icon lms-thumbnail-upload-btn">
                                  <i className="fas fa-upload"></i>
                                  {t("admin.lms.course.uploadImage", "Upload image")}
                                </span>
                              </span>
                            )}
                          </label>
                          {previewUrl && (
                            <div className="lms-thumbnail-actions d-flex justify-content-center items-center ">
                              <label
                                className="btn secondary-btn btn-with-icon mt-2"
                                htmlFor="course-thumbnail-upload"
                              >
                                <i className="fas fa-upload"></i>
                                {t("admin.lms.course.changeImage", "Change image")}
                              </label>
                            </div>
                          )}
                          {thumbnailFile && (
                            <div className="lms-upload-file">
                              <i className="fas fa-check-circle"></i>
                              {thumbnailFile.name}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
                {false && step === 1 && (
                  <div className="card lms-form-card mb-3">
                    <div className="card-body">
                      <h3 className="lms-section-title">
                        {t("admin.lms.wizard.step_info")}
                      </h3>
                      <div className="row form-own">
                        <div className="form-own-group col-12">
                          <label className="form-own-label">
                            {t("admin.lms.fields.courseTitle")} *
                          </label>
                          <input
                            className="form-control"
                            value={form.title}
                            onChange={(e) => setField("title", e.target.value)}
                          />
                        </div>
                        <div className="form-own-group col-12">
                          <label className="form-own-label">
                            {t("common.description")}
                          </label>
                          <textarea
                            className="form-control own-textarea"
                            rows={5}
                            value={form.description}
                            onChange={(e) =>
                              setField("description", e.target.value)
                            }

                          />
                        </div>
                      </div>

                      <div className="lms-course-media-row lms-course-media-row-single">
                        <div className="lms-course-media-panel lms-course-thumbnail-panel">
                          <div className="lms-course-media-head">
                            <h4 className="lms-section-title">
                              {t("admin.lms.fields.thumbnail")}
                            </h4>
                          </div>
                          <input
                            id="course-thumbnail-upload"
                            type="file"
                            className="lms-file-input"
                            accept="image/*"
                            onChange={(e) => {
                              const file = e.target.files?.[0] || null;
                              setThumbnailFile(file);

                              if (file) {
                                setPreviewUrl(URL.createObjectURL(file));
                              } else {
                                setPreviewUrl(null);
                              }
                            }}
                          />
                          <label
                            className={`lms-thumb-uploader ${previewUrl ? "has-preview" : ""
                              }`}
                            htmlFor="course-thumbnail-upload"
                          >
                            {previewUrl ? (
                              <img src={previewUrl} alt="Course thumbnail" />
                            ) : (
                              <span className="lms-upload-placeholder">
                                <i className="fas fa-image"></i>
                                <span>
                                  {"Upload Course Thumbnail"}
                                </span>
                              </span>
                            )}
                          </label>
                          <div className="lms-upload-actions">
                            <label
                              className="btn btn-sm secondary-btn btn-with-icon mb-0"
                              htmlFor="course-thumbnail-upload"
                            >
                              <i className="fas fa-upload"></i>
                              {previewUrl ? "Change image" : "Upload image"}
                            </label>
                            {/* {previewUrl && (
                              <button
                                type="button"
                                className="btn btn-sm secondary-btn btn-own-grey btn-with-icon"
                                onClick={() => setShowModal(true)}
                              >
                                <i className="fas fa-eye"></i>
                                Preview
                              </button>
                            )} */}
                          </div>
                          {thumbnailFile && (
                            <div className="lms-upload-file">
                              <i className="fas fa-check-circle"></i>
                              {thumbnailFile.name}
                            </div>
                          )}

                          {showModal && previewUrl && (
                            <div
                              className="modal fade show"
                              style={{
                                display: "block",
                                backgroundColor: "rgba(0,0,0,0.5)",
                              }}
                              role="dialog"
                            >
                              <div
                                className="modal-dialog modal-dialog-centered"
                                role="document"
                              >
                                <div className="modal-content">
                                  <div className="modal-header d-flex justify-content-between align-items-center">
                                    <h5 className="modal-title">
                                      {t("admin.lms.fields.thumbnail")} Preview
                                    </h5>
                                    <button
                                      type="button"
                                      className="close btn"
                                      onClick={() => setShowModal(false)}
                                      style={{ fontSize: "1.5rem" }}
                                    >
                                      <span>&times;</span>
                                    </button>
                                  </div>
                                  <div className="modal-body text-center">
                                    <img
                                      src={previewUrl}
                                      alt="Uploaded Thumbnail"
                                      className="lms-thumb-modal-img"
                                    />
                                  </div>
                                  <div className="modal-footer">
                                    <button
                                      type="button"
                                      className="btn secondary-btn btn-with-icon"
                                      onClick={() => setShowModal(false)}
                                    >
                                      Close
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* ── Step 2: Course intro videos ── */}
                {step === 2 && (
                  <div className="card lms-form-card lms-intro-step-card mb-3">
                    <div className="card-body">
                      <div className="lms-intro-step-header">
                        <span className="lms-intro-step-icon">
                          <i className="fas fa-video"></i>
                        </span>
                        <div>
                          <h3>{t("admin.lms.course.introVideos")}</h3>
                          <p>
                            {t(
                              "admin.lms.courseIntro.subtitle",
                              "Upload the service overview videos that learners will watch before starting the course modules."
                            )}
                          </p>
                        </div>
                      </div>
                      {/* 
                      <div className="lms-intro-why-box">
                        <i className="fas fa-info-circle"></i>
                        <div>
                          <strong>
                            {t(
                              "admin.lms.courseIntro.whyTitle",
                              "Why are these videos required?"
                            )}
                          </strong>
                          <span>
                            {t(
                              "admin.lms.courseIntro.whyBody",
                              "Every course begins with a service overview video. Since ESHS and SSHS provide different services, each requires its own introduction video to help learners understand what to expect."
                            )}
                          </span>
                        </div>
                      </div> */}
                      <div className="lms-course-intro-step">
                        {apiCourseId ? (
                          <LmsAudienceAssets
                            variant="courseIntro"
                            values={SERVICE_AUDIENCES}
                            items={course?.introAssets || []}
                            wrapKey="serviceAudience"
                            accept="video/mp4,video/webm,video/quicktime"
                            uploadContext={(audience) => ({
                              assetRole: "course-intro",
                              courseId: apiCourseId,
                              courseSlug: course?.slug,
                              courseTitle: course?.title || form.title,
                              serviceAudience: audience,
                            })}
                            labelFor={(v) =>
                              t(`admin.lms.audience.${v}`, v)
                              //  +
                              // " " +
                              // t("admin.lms.course.introVideoSuffix")
                            }
                            onAdd={addCourseIntro}
                            onRemove={removeCourseIntro}
                          />
                        ) : (
                          <div className="lms-intro-locked">
                            <div className="lms-upload-placeholder">
                              <i className="fas fa-video"></i>
                              <span>{t("admin.lms.course.saveToAddIntro")}</span>
                            </div>
                            <button
                              type="button"
                              className="btn btn-sm secondary-btn btn-with-icon"
                              onClick={saveStay}
                            >
                              <i className="fas fa-save"></i>
                              {t("admin.lms.course.saveCourse")}
                            </button>
                          </div>
                        )}
                      </div>

                      {/* <div className="lms-intro-best-practice">
                        <i className="far fa-star"></i>
                        <div>
                          <strong>
                            {t(
                              "admin.lms.courseIntro.bestPracticeTitle",
                              "Best practice"
                            )}
                          </strong>
                          <span>
                            {t(
                              "admin.lms.courseIntro.bestPracticeBody",
                              "Keep introduction videos short and engaging (2-5 minutes) to retain learner interest."
                            )}
                          </span>
                        </div>
                      </div> */}
                    </div>
                  </div>
                )}

                {/* ── Step 3: Learning content (split view) ── */}
                {step === 3 && (
                  <div className="mb-3">
                    <LmsLearningContentStep
                      courseId={apiCourseId}
                      course={course}
                      modules={modules}
                      onRefresh={loadCourse}
                      registerUnsavedChangesGuard={(guard) => {
                        learningContentGuardRef.current = guard;
                      }}
                      setWizardDirty={setWizardDirty}
                    />
                  </div>
                )}

                {/* ── Step 4: Review & Publish ── */}
                {step === 4 && (
                  <div className="card lms-form-card lms-review-publish-card mb-3">
                    <div className="card-body">
                      <div className="lms-review-header">
                        <div className="course-finish-view">
                          <h3 className="lms-section-title mb-1">
                            {t("admin.lms.wizard.step_reviewPublish")}
                          </h3>
                          <p className="lms-review-subtitle mb-0">
                            {published
                              ? t(
                                "admin.lms.wizard.coursePublished",
                                "This course has been published and is now available to learners."
                              )
                              : publishReady
                                ? t(
                                  "admin.lms.wizard.readyToPublish",
                                  "Everything required is ready for publishing."
                                )
                                : t(
                                  "admin.lms.wizard.finishRequiredItems",
                                  "Complete the required items before publishing."
                                )}
                          </p>
                        </div>
                        <div className="lms-publish-action-buttons lms-publish-action-buttons-top">
                          <button
                            className="btn secondary-btn btn-own-grey btn-with-icon"
                            onClick={openLearnerPreview}
                            disabled={!courseRouteIdentifier}
                          >
                            <i className="fas fa-eye mr-1"></i>
                            {t("admin.lms.preview.previewAsLearner")}
                          </button>

                          <button
                            className="btn secondary-btn btn-with-icon lms-publish-now-btn"
                            onClick={publishCourse}
                            disabled={!publishReady || (published && !wizardDirty)}
                            title={
                              !publishReady
                                ? step3Issues[0] || ""
                                : published && !wizardDirty
                                  ? "No changes to save"
                                  : ""
                            }
                          >
                            <i
                              className={`fas ${published ? "fa-save" : "fa-rocket"
                                } mr-1`}
                            ></i>

                            {published
                              ? "Save Changes"
                              : t("admin.lms.wizard.publishNow", "Publish Now")}
                          </button>
                        </div>
                      </div>

                      <div className="lms-review-checklist">
                        {serverReadiness ? (
                          <LmsReadinessPanel readiness={reviewReadiness} hideHeader />
                        ) : (
                          readiness.checks.map((c) => (
                            <div className="lms-readiness-row" key={c.key}>
                              <i
                                className={`fas ${c.ok
                                  ? "fa-check-circle text-success"
                                  : c.warn
                                    ? "fa-exclamation-triangle text-warning"
                                    : "fa-times-circle text-danger"
                                  } mr-2`}
                              ></i>
                              {c.label}
                            </div>
                          ))
                        )}
                        {/* <div className="lms-review-panel">
                          <div className="lms-review-panel-head">
                            <span className="lms-review-panel-icon">
                              <i className="fas fa-list-check"></i>
                            </span>
                            <div>
                              <h4>{t("admin.lms.wizard.prePublishHeading")}</h4>
                              <p>{t("admin.lms.wizard.requiredBeforePublish", "Required before publish")}</p>
                            </div>
                          </div>
                          <div className="lms-review-checklist">
                            {prePublishItems.map((item) => (
                              <div
                                className={`lms-review-check-item ${item.ok ? "is-ok" : "is-missing"
                                  }`}
                                key={item.key}
                              >
                                <i
                                  className={`fas ${item.ok ? "fa-check-circle" : "fa-times-circle"
                                    }`}
                                ></i>
                                <div>
                                  <strong>{item.label}</strong>
                                  <span>{item.detail}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </div> */}
                      </div>

                    </div>
                  </div>
                )}

                {/* Footer nav */}
                <div
                  className="lms-wizard-footer  d-flex justify-content-between"

                >
                  <div>
                    <button
                      className="btn secondary-btn btn-own-grey"
                      onClick={() =>
                        runWithLearningContentGuard(() =>
                          step > 1
                            ? back()
                            : props?.history?.push("/admin/lms/courses")
                        )
                      }
                    >
                      <i className="fas fa-arrow-left me-2"></i>
                      {t("admin.lms.wizard.back")}
                    </button>
                  </div>
                  <div className="d-flex align-items-center w-100">
                    {step < LAST_STEP ? (
                      <>
                        {/* Left spacer */}
                        <div className="flex-fill"></div>

                        {/* Center */}
                        <div className="text-center">
                          {!wizardDirty ? (
                            <button
                              className="btn secondary-btn btn-own-grey"
                              onClick={() => props?.history?.push("/admin/lms/courses")}
                            >
                              {t("admin.lms.course.exit", "Exit")}
                            </button>
                          ) : (
                            <button
                              className="btn secondary-btn btn-own-grey"
                              onClick={() =>
                                runWithLearningContentGuard(saveDraftAndExit)
                              }
                            >
                              {t("admin.lms.course.saveDraft")}
                            </button>
                          )}
                        </div>

                        {/* Right */}
                        <div className="flex-fill d-flex justify-content-end">
                          <button
                            className="btn secondary-btn"
                            onClick={() => runWithLearningContentGuard(next)}
                            disabled={nextBlocked}
                            title={
                              nextBlocked
                                ? step === 1
                                  ? t("admin.lms.course.titleRequired", "Title is required")
                                  : step === 3
                                  ? step3NavigationIssues[0]
                                  : ""
                                : ""
                            }
                          >
                            {nextStepLabel}
                            <i className="fas fa-arrow-right ms-2"></i>
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="w-100 d-flex justify-content-end">
                        {!wizardDirty ? (
                          <button
                            className="btn secondary-btn btn-own-grey"
                            onClick={() => props?.history?.push("/admin/lms/courses")}
                          >
                            {t("admin.lms.course.exit", "Exit")}
                          </button>
                        ) : (
                          <button
                            className="btn secondary-btn btn-own-grey"
                            onClick={() =>
                              runWithLearningContentGuard(saveDraftAndExit)
                            }
                          >
                            {t("admin.lms.course.saveDraft")}
                          </button>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {learningStatusModal && (
        <div
          className="modal fade show lms-learning-status-modal"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.45)" }}
          role="dialog"
          aria-modal="true"
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header" style={{ backgroundColor: '#322F91', borderTopLeftRadius: 'calc(.3rem - 1px)', borderTopRightRadius: 'calc(.3rem - 1px)' }}>
                <div className="lms-learning-status-title">
                  <span className="lms-learning-status-icon">
                    <i
                      className={`fas ${learningStatusModal.status.remainingModules
                        ? "fa-exclamation-triangle text-danger"
                        : "fa-check text-success"
                        }`}
                    ></i>
                  </span>
                  <div>
                    <h5 className="modal-title" style={{ color: 'white' }}>
                      {t(
                        "admin.lms.wizard.learningContentStatus",
                        "Learning content status"
                      )}
                    </h5>
                  </div>
                </div>
              </div>
              <div className="modal-body">
                <div className="lms-learning-status-summary">
                  <div className="lms-learning-status-card">
                    <strong>{learningStatusModal.status.completedModules}</strong>
                    <span>
                      {t("admin.lms.wizard.completedModules", "Completed Modules")}
                    </span>
                  </div>
                  <div className="lms-learning-status-card">
                    <strong>{learningStatusModal.status.remainingModules}</strong>
                    <span>
                      {t("admin.lms.wizard.remainingModules", "Remaining Modules")}
                    </span>
                  </div>
                  <div className="lms-learning-status-card">
                    <strong>
                      {learningStatusModal.status.completedItems}/
                      {learningStatusModal.status.totalItems}
                    </strong>
                    <span>
                      {t("admin.lms.wizard.requiredItems", "Required Items")}
                    </span>
                  </div>
                </div>

                {learningStatusModal.status.remaining.length ? (
                  <div className="lms-learning-status-list">
                    <h6>
                      {t("admin.lms.wizard.remainingItems", "Remaining items")}
                    </h6>
                    {learningStatusModal.status.remaining.map((item, index) => (
                      <div
                        className="lms-learning-status-module"
                        key={`${item.moduleTitle}-${index}`}
                        style={{ backgroundColor: '#fff3cd' }}
                      >
                        <strong>{item.moduleTitle}</strong>
                        <ul>
                          {item.missing.map((missingItem) => (
                            <li key={missingItem}>{missingItem}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="lms-learning-status-complete">
                    <i className="fas fa-check-circle"></i>
                    <span>
                      {t(
                        "admin.lms.wizard.allContentComplete",
                        "All module content requirements are complete."
                      )}
                    </span>
                  </div>
                )}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn secondary-btn btn-own-grey"
                  onClick={() => setLearningStatusModal(null)}
                >
                  {t("admin.lms.wizard.reviewContent", "Review content")}
                </button>
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={confirmLearningStatusAndNext}
                >
                  {t("common.ok", "OK")}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default withRouter(LmsCourseWizard);
